import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrganization } from '../../context/OrganizationContext';
import { db } from '../../firebase';
import { collection, onSnapshot, query, where, doc } from 'firebase/firestore';
import { organizationsAPI } from '../../services/api';
import { ASSIGNABLE_ROLES } from '../../utils/rolesMatrix';
import './OrganizationSettings.css';

export default function OrganizationSettings() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { user, context } = useOrganization();

  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    // Real-time organization data
    const unsubOrg = onSnapshot(
      doc(db, 'organizations', orgId),
      (doc) => {
        if (doc.exists()) {
          setOrganization({ id: doc.id, ...doc.data() });
        } else {
          console.error('Organization not found');
          navigate('/');
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching organization:', error);
        setLoading(false);
      }
    );

    // Real-time members
    const unsubMembers = onSnapshot(
      query(
        collection(db, 'organizations', orgId, 'members'),
        where('status', '==', 'active')
      ),
      (snapshot) => {
        const membersList = snapshot.docs
          .map(doc => ({ uid: doc.id, ...doc.data() }))
          .sort((a, b) => {
            // Sort: owner first, then by joinedAt
            if (a.role === 'owner') return -1;
            if (b.role === 'owner') return 1;
            return a.joinedAt?.toMillis() - b.joinedAt?.toMillis();
          });
        setMembers(membersList);
      },
      (error) => {
        console.error('Error fetching members:', error);
      }
    );

    // Real-time invitations
    const unsubInvites = onSnapshot(
      query(
        collection(db, 'organizations', orgId, 'invitations'),
        where('status', '==', 'pending')
      ),
      (snapshot) => {
        const invitesList = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis());
        setInvitations(invitesList);
      },
      (error) => {
        console.error('Error fetching invitations:', error);
      }
    );

    return () => {
      unsubOrg();
      unsubMembers();
      unsubInvites();
    };
  }, [orgId, user, navigate]);

  const handleInviteMember = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteLoading(true);

    try {
      await organizationsAPI.inviteMember(orgId, inviteEmail, inviteRole);

      // Success
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      alert('Invitación enviada exitosamente');
    } catch (error) {
      setInviteError(error.message || 'Error al enviar invitación');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('¿Cancelar esta invitación?')) return;

    try {
      await organizationsAPI.cancelInvitation(orgId, inviteId);
      alert('Invitación cancelada');
    } catch (error) {
      alert(error.message || 'Error al cancelar invitación');
    }
  };

  if (loading) {
    return (
      <div className="org-settings-loading">
        <div className="spinner"></div>
        <p>Cargando organización...</p>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="org-settings-error">
        <p>Organización no encontrada</p>
      </div>
    );
  }

  const canInvite = context.permissions?.canInviteMembers;
  const canManageMembers = context.permissions?.canManageMembers;

  return (
    <div className="org-settings">
      <div className="org-settings-header">
        <div>
          <h1>{organization.name}</h1>
          <p className="org-type">{organization.type === 'personal' ? '👤 Personal' : '🏢 Team'}</p>
        </div>
        <button onClick={() => navigate(-1)} className="btn-back">
          ← Volver
        </button>
      </div>

      {/* Organization Info */}
      <section className="org-section">
        <h2>Información</h2>
        <div className="org-info-grid">
          <div className="info-card">
            <span className="info-label">Plan</span>
            <span className="info-value">{organization.planId || 'starter'}</span>
          </div>
          <div className="info-card">
            <span className="info-label">Miembros</span>
            <span className="info-value">
              {organization.seats?.total - organization.seats?.available} / {organization.seats?.total}
            </span>
          </div>
          <div className="info-card">
            <span className="info-label">Estado</span>
            <span className="info-value status" data-status={organization.billing?.status}>
              {organization.billing?.status || 'trial'}
            </span>
          </div>
        </div>
      </section>

      {/* Members */}
      <section className="org-section">
        <div className="section-header">
          <h2>Miembros ({members.length})</h2>
          {canInvite && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="btn-primary"
            >
              + Invitar Miembro
            </button>
          )}
        </div>

        <div className="members-list">
          {members.map(member => (
            <div key={member.uid} className="member-card">
              <div className="member-info">
                <div className="member-avatar">
                  {member.displayName?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase()}
                </div>
                <div className="member-details">
                  <p className="member-name">{member.displayName || member.email}</p>
                  <p className="member-email">{member.email}</p>
                </div>
              </div>
              <div className="member-meta">
                <span className={`role-badge ${member.role}`}>
                  {member.role}
                </span>
                {member.uid === user?.uid && (
                  <span className="you-badge">Tú</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pending Invitations */}
      {canManageMembers && invitations.length > 0 && (
        <section className="org-section">
          <h2>Invitaciones Pendientes ({invitations.length})</h2>
          <div className="invitations-list">
            {invitations.map(invite => (
              <div key={invite.id} className="invitation-card">
                <div className="invitation-info">
                  <p className="invitation-email">{invite.email}</p>
                  <p className="invitation-role">Rol: {invite.role}</p>
                  <p className="invitation-date">
                    Enviada {new Date(invite.createdAt?.toDate()).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => handleCancelInvite(invite.id)}
                  className="btn-cancel"
                >
                  Cancelar
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Invitar Miembro</h2>
            <form onSubmit={handleInviteMember}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="usuario@ejemplo.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Rol</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  {/* Roles asignables (rolesMatrix, sin viewer). */}
                  {ASSIGNABLE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {{ admin: 'Admin - Puede gestionar miembros', member: 'Member - Puede editar' }[r] || r}
                    </option>
                  ))}
                </select>
              </div>

              {inviteError && (
                <div className="error-message">{inviteError}</div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={inviteLoading}
                  className="btn-primary"
                >
                  {inviteLoading ? 'Enviando...' : 'Enviar Invitación'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
